import React, { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import SideBar from "./SideBar";
import "./AdminLayout.css";
import "./header.css";
import PageLoader from "../../components/shared/PageLoader";
import AdminLoginModal from "../../components/admin/AdminLoginModal";
import { websitePaths } from "../../routes/websitePaths";
import ModalBackdropCleanup from "../../components/shared/ModalBackdropCleanup";
import { useAdminAuth } from "../../hooks/useAdminAuth";
import {
  useFetchAdminProfileQuery,
  getFullAvatarUrl,
} from "../../store/api/adminApi";
import { useFetchRequestsQuery } from "../../store/api/requestsApi";

const allMenuItems = [
  { to: "/admin", label: "Dashboard", icon: "bi bi-grid-1x2-fill", group: "Overview" },
  { to: "/admin/cost-estimator", label: "Cost Estimator", icon: "bi bi-calculator-fill", group: "Overview" },
  { to: "/admin/requests",      label: "Requests",             icon: "bi bi-inboxes-fill", group: "Management" },
  { to: "/admin/notifications", label: "Send Notification",    icon: "bi bi-send-fill",    group: "Management" },
  { to: "/admin/projects", label: "Projects", icon: "bi bi-kanban-fill", group: "Management" },
  { to: "/admin/projects/progress", label: "Task Progress", icon: "bi bi-list-check", group: "Management" },
  { to: "/admin/projects/progress-analytics", label: "Progress Charts", icon: "bi bi-bar-chart-line-fill", group: "Management" },
  { to: "/admin/employees", label: "Employees", icon: "bi bi-people-fill", group: "People" },
  { to: "/admin/clients", label: "Clients", icon: "bi bi-people-fill", group: "People" },
  { to: "/admin/expenses", label: "Expenses", icon: "bi bi-wallet2", group: "People" },
  { to: "/admin/payments", label: "Payments", icon: "bi bi-cash-coin", group: "People" },
  { to: "/admin/reviews",      label: "Reviews",      icon: "bi bi-chat-square-quote-fill", group: "People" },
  { to: "/admin/page-images",  label: "Page Images",  icon: "bi bi-images",                group: "Management" },
  { to: "/admin/contact-settings", label: "Site Settings",  icon: "bi bi-gear-fill",      group: "Account" },
  { to: "/admin/profile",          label: "Profile",        icon: "bi bi-person-circle",  group: "Account" },
  { to: "/admin/manage-passwords", label: "Manage Passwords", icon: "bi bi-shield-lock-fill", group: "Account" },
];

const pageTitles: Record<string, string> = {
  "/admin": "Dashboard",
  "/admin/requests": "Request Management",
  "/admin/notifications": "Send Notification",
  "/admin/projects": "Project List",
  "/admin/projects/new": "Add Project",
  "/admin/projects/progress": "Task Progress",
  "/admin/projects/progress-analytics": "Progress Analytics",
  "/admin/project-images": "Project Images",
  "/admin/reviews": "Reviews",
  "/admin/profile": "Profile",
  "/admin/clients": "Clients",
  "/admin/expenses": "Expenses",
  "/admin/payments": "Payments",
  "/admin/cost-estimator": "Cost Estimator",
  "/admin/employees": "Employees",
  "/admin/employees/new": "Add Employee",
  "/admin/manage-passwords":  "Manage Passwords",
  "/admin/contact-settings":  "Site Settings",
  "/admin/page-images":       "Page Images",
};

const BELL_STORAGE_KEY = "admin_bell_last_seen";

const Header = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { adminUser, logout } = useAdminAuth();

  const { data: serverProfile } = useFetchAdminProfileQuery(undefined, {
    skip: !adminUser,
  });

  // Poll every 30 s so the bell and dashboard stay fresh without a page refresh
  const { data: requestsData = [] } = useFetchRequestsQuery(undefined, {
    skip: !adminUser,
    pollingInterval: 60_000,
  });

  // ── Derived request counts ────────────────────────────────────────
  // "new" = just submitted by customer, "pending" = legacy/normalized status
  const pendingRequests = requestsData
    .filter((r) => r.status === "new" || r.status === "pending")
    .sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    });

  const [lastSeenAt, setLastSeenAt] = useState<string>(
    () => localStorage.getItem(BELL_STORAGE_KEY) ?? "",
  );

  const lastSeenTime = lastSeenAt ? new Date(lastSeenAt).getTime() : 0;
  const newCount = pendingRequests.filter(
    (r) => !lastSeenAt || new Date(r.createdAt).getTime() > lastSeenTime,
  ).length;

  // ── UI state ──────────────────────────────────────────────────────
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLDivElement>(null);
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // ── Search results ────────────────────────────────────────────────
  const searchResults = (() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return allMenuItems;
    return allMenuItems.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.group.toLowerCase().includes(q),
    );
  })();

  const avatarSrc = getFullAvatarUrl(serverProfile?.avatarUrl ?? null);
  const initial = adminUser
    ? adminUser.name.trim().charAt(0).toUpperCase() || "A"
    : "A";
  const pageTitle = pageTitles[pathname] ?? "Admin Panel";

  // ── Ctrl+K / Cmd+K → focus search ────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setSearchOpen(true);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // ── Close all panels on outside click ────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) {
        setBellOpen(false);
      }
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Bell helpers ──────────────────────────────────────────────────
  const markAllSeen = () => {
    const now = new Date().toISOString();
    localStorage.setItem(BELL_STORAGE_KEY, now);
    setLastSeenAt(now);
  };

  // ── Search helpers ────────────────────────────────────────────────
  const closeSearch = (to?: string) => {
    if (to) navigate(to);
    setSearchQuery("");
    setSearchOpen(false);
    setActiveIndex(-1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!searchOpen) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, searchResults.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, -1));
        break;
      case "Enter":
        if (activeIndex >= 0 && searchResults[activeIndex]) {
          closeSearch(searchResults[activeIndex].to);
        }
        break;
      case "Escape":
        closeSearch();
        inputRef.current?.blur();
        break;
    }
  };

  const handleLogout = () => {
    setDropdownOpen(false);
    logout();
    navigate(websitePaths.home);
  };

  if (!adminUser) {
    return (
      <div className='admin-auth-guard'>
        <ModalBackdropCleanup />
        <PageLoader variant='admin' />
        <div className='admin-auth-card'>
          <p className='admin-header-kicker mb-2'>Restricted Access</p>
          <h1 className='admin-header-title mb-2'>Admin login required</h1>
          <p className='admin-panel-subtitle mb-0'>
            Sign in to access requests, project images, reviews, and profile
            management.
          </p>
        </div>
        <AdminLoginModal show forceOpen />
      </div>
    );
  }

  return (
    <div className='admin-shell'>
      <ModalBackdropCleanup />

      {/* Sidebar overlay */}
      <div
        className={`admin-sidebar-overlay${sidebarOpen ? " admin-sidebar-overlay--visible" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* ── Top Header ─────────────────────────────────────────── */}
      <header className='admin-header nh-header'>

        {/* Left: hamburger + page title */}
        <div className='nh-left'>
          <button
            type='button'
            className='admin-hamburger'
            aria-label='Open navigation menu'
            onClick={() => setSidebarOpen(true)}
          >
            <i className='bi bi-list' />
          </button>
          <div className='nh-page-title-wrap'>
            <span className='nh-breadcrumb'>
              <i className='bi bi-grid-1x2-fill' /> Admin
            </span>
            <h1 className='nh-page-title'>{pageTitle}</h1>
          </div>
        </div>

        {/* Center: search bar */}
        <div className='nh-search-wrap' ref={searchWrapRef}>
          <i className='bi bi-search nh-search-icon' />
          <input
            ref={inputRef}
            type='text'
            className='nh-search-input'
            placeholder='Search menu…'
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSearchOpen(true);
              setActiveIndex(-1);
            }}
            onFocus={() => setSearchOpen(true)}
            onKeyDown={handleSearchKeyDown}
            aria-label='Menu search'
            autoComplete='off'
          />

          {searchQuery ? (
            <button
              type='button'
              className='nh-search-clear'
              aria-label='Clear search'
              tabIndex={-1}
              onMouseDown={(e) => {
                e.preventDefault();
                setSearchQuery("");
                setActiveIndex(-1);
                inputRef.current?.focus();
              }}
            >
              <i className='bi bi-x' />
            </button>
          ) : (
            <kbd className='nh-search-kbd'>Ctrl K</kbd>
          )}

          {searchOpen && (
            <div className='nh-search-results'>
              {searchQuery.trim() === "" && (
                <div className='nh-search-results-label'>Quick navigation</div>
              )}
              {searchResults.length === 0 ? (
                <div className='nh-search-no-results'>No menu items found</div>
              ) : (
                searchResults.map((item, idx) => (
                  <button
                    key={item.to}
                    type='button'
                    className={`nh-search-result-item${activeIndex === idx ? " nh-search-result-item--active" : ""}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      closeSearch(item.to);
                    }}
                    onMouseEnter={() => setActiveIndex(idx)}
                  >
                    <i className={item.icon} />
                    <span className='nh-search-result-label'>{item.label}</span>
                    <span className='nh-search-result-group'>{item.group}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Right: bell + user dropdown */}
        <div className='nh-right'>

          {/* ── Bell ── */}
          <div className='nh-bell-wrap' ref={bellRef}>
            <button
              type='button'
              className='nh-icon-btn'
              aria-label={newCount > 0 ? `${newCount} new pending requests` : "Notifications"}
              onClick={() => {
                setBellOpen((o) => !o);
                if (!bellOpen && newCount > 0) markAllSeen();
              }}
            >
              <i className={newCount > 0 ? "bi bi-bell-fill" : "bi bi-bell"} />
              {newCount > 0 && (
                <span className='nh-badge'>{newCount > 99 ? "99+" : newCount}</span>
              )}
            </button>

            {bellOpen && (
              <div className='nh-bell-dropdown'>
                <div className='nh-bell-header'>
                  <span className='nh-bell-title'>Pending Requests</span>
                  <span className='nh-bell-count'>{pendingRequests.length} total</span>
                </div>

                {pendingRequests.length === 0 ? (
                  <div className='nh-bell-empty'>
                    <i className='bi bi-check-circle' />
                    <span>No pending requests</span>
                  </div>
                ) : (
                  <div className='nh-bell-list'>
                    {pendingRequests.slice(0, 6).map((req) => {
                      const isNew = !lastSeenAt || req.createdAt > lastSeenAt;
                      return (
                        <Link
                          key={req.id}
                          to='/admin/requests'
                          className={`nh-bell-item${isNew ? " nh-bell-item--new" : ""}`}
                          onClick={() => setBellOpen(false)}
                        >
                          <div className='nh-bell-avatar'>
                            {req.name.trim().charAt(0).toUpperCase()}
                          </div>
                          <div className='nh-bell-body'>
                            <div className='nh-bell-name'>{req.name}</div>
                            <div className='nh-bell-meta'>
                              {req.projectType}
                              {req.city ? ` · ${req.city}` : ""}
                            </div>
                          </div>
                          {isNew && <span className='nh-bell-dot' aria-label='New' />}
                        </Link>
                      );
                    })}
                  </div>
                )}

                <Link
                  to='/admin/requests'
                  className='nh-bell-footer'
                  onClick={() => setBellOpen(false)}
                >
                  View all requests
                  <i className='bi bi-arrow-right' />
                </Link>
              </div>
            )}
          </div>

          {/* ── User dropdown ── */}
          <div className='nh-user-wrap' ref={dropdownRef}>
            <button
              type='button'
              className='nh-avatar-btn'
              onClick={() => setDropdownOpen((o) => !o)}
              aria-haspopup='true'
              aria-label='User menu'
            >
              {avatarSrc ? (
                <img src={avatarSrc} alt='Profile' className='nh-avatar-img' />
              ) : (
                <span className='nh-avatar-initial'>{initial}</span>
              )}
              <div className='nh-user-info d-none d-md-block'>
                <span className='nh-user-name'>{adminUser.name || "Admin"}</span>
              </div>
              <i
                className={`bi bi-chevron-${dropdownOpen ? "up" : "down"} nh-chevron d-none d-md-inline`}
              />
            </button>

            {dropdownOpen && (
              <div className='nh-dropdown'>
                <div className='nh-dropdown-header'>
                  {avatarSrc ? (
                    <img src={avatarSrc} alt='Profile' className='nh-dropdown-avatar' />
                  ) : (
                    <span className='nh-dropdown-initial'>{initial}</span>
                  )}
                  <div>
                    <div className='nh-dropdown-name'>{adminUser.name || "Admin"}</div>
                    <div className='nh-dropdown-email'>{adminUser.email}</div>
                  </div>
                </div>

                <div className='nh-dropdown-divider' />

                <Link
                  to='/admin/profile'
                  className='nh-dropdown-item'
                  onClick={() => setDropdownOpen(false)}
                >
                  <i className='bi bi-person-circle' />
                  <span>My Profile</span>
                </Link>

                <Link
                  to='/admin/manage-passwords'
                  className='nh-dropdown-item'
                  onClick={() => setDropdownOpen(false)}
                >
                  <i className='bi bi-key' />
                  <span>Change Password</span>
                </Link>

                <div className='nh-dropdown-divider' />

                <button
                  type='button'
                  className='nh-dropdown-item nh-dropdown-item--danger'
                  onClick={handleLogout}
                >
                  <i className='bi bi-box-arrow-right' />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <SideBar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <main id='main' className='admin-main'>
        <Outlet />
      </main>
    </div>
  );
};

export default Header;
